import { SiteUrls } from '@tableau/shared-urls';
import { DataGrid } from '@tableau/data-grid-1-wrapper';
import * as React from 'react';
import { defaultMemoize } from 'reselect/lib';

import { FlowOverviewDataGridRow, FlowOverviewGrid } from '../../app/content/FlowOverviewGrid';
import { ServiceLocator } from '../../app/ServiceLocator';
import { Messages } from '../../codegen/Localize';
import { CommonStyles } from '../../common/react/CommonStyles';
import { getHeightForRows, SortableDataGrid } from '../../common/react/components/dataGrid/SortableDataGrid';
import { cssClass, flexColumnStyle } from '../../style';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { FlowBaseInfo } from '../../ts/models/FlowInfo';
import { FlowOutputStepInfo } from '../../ts/models/FlowOutputStepInfo';
import { FlowOutputStepRunListItemInfo, FlowOutputStepRunStatus } from '../../ts/models/FlowOutputStepRunInfo';
import { ISliceable } from '../../ts/models/ISliceable';
import { RunFlowTaskInfo } from '../../ts/models/RunFlowTaskInfo';
import { FlowAllowedActions } from '../../ts/ServerApi/types/FlowAllowedActions';
import { IOrderClause } from '../../ts/ServerApi/types/IOrderClause';
import { SliceableFactory } from '../../ts/services/SliceableFactory';
import { grey230border } from '../styles/FlowStyleConstants';

const updateFlowOverviewGridIntervalSeconds = 60;

const cssPrefix = 'FlowOverviewDataGridView';

const rootClass = cssClass(cssPrefix, 'root', flexColumnStyle, {
  position: 'relative',
  minHeight: getHeightForRows(2),
  paddingBottom: CommonStyles.Sizes.LargeSpace,
  borderBottom: grey230border
});

function getTasksForOutput(runFlowTasks: ReadonlyArray<RunFlowTaskInfo>, outputId: string): RunFlowTaskInfo[] {
  return runFlowTasks.filter((task) => {
    return task.flowOutputSteps.find((step) => step && step.id === outputId);
  });
}

function createRowData(
  flowInfo: FlowBaseInfo,
  runFlowTasks: ReadonlyArray<RunFlowTaskInfo>,
  outputStepInfos: ReadonlyArray<FlowOutputStepInfo>,
  outputStepRuns: ReadonlyArray<FlowOutputStepRunListItemInfo>
): FlowOverviewDataGridRow[] {
  return outputStepInfos.map(output => {
    return {
      runOutput: { flowInfo: flowInfo, outputId: output.id },
      flowOutputStep: output,
      outputStepMostRecentRun: outputStepRuns.find(run => run.flowOutputStepId === output.id),
      schedules: getTasksForOutput(runFlowTasks, output.id),
    };
  });
}

function getRowData(
  sliceableFactory: SliceableFactory,
  flowInfo: FlowBaseInfo,
  runFlowTasks: ReadonlyArray<RunFlowTaskInfo>,
  outputStepInfos: ReadonlyArray<FlowOutputStepInfo>,
  outputStepRuns: ReadonlyArray<FlowOutputStepRunListItemInfo>
): (orderClause: IOrderClause[]) => ISliceable<FlowOverviewDataGridRow> {
  return (orderClause: IOrderClause[]) =>
    sliceableFactory.fromArray(createRowData(flowInfo, runFlowTasks, outputStepInfos, outputStepRuns));
}

function createColumns(
  flowInfo: FlowBaseInfo,
  flowAllowedActions: FlowAllowedActions,
  isServerOrSiteAdmin: boolean,
  siteUrls: SiteUrls,
  updateData: () => void,
  isIncrementalEnabled: boolean,
  schedulingFlowsEnabled: boolean
): DataGrid.Column[] {
  return FlowOverviewGrid.columns(
    flowInfo,
    flowAllowedActions,
    isServerOrSiteAdmin,
    siteUrls,
    updateData,
    isIncrementalEnabled,
    schedulingFlowsEnabled
  );
}

export type FlowOverviewDataGridViewProps = {
  readonly flowInfo: FlowBaseInfo;
  readonly updateData: () => void;
  readonly siteUrls: SiteUrls;
  readonly outputStepInfos: ReadonlyArray<FlowOutputStepInfo>;
  readonly outputStepRuns: ReadonlyArray<FlowOutputStepRunListItemInfo>;
  readonly runFlowTasks: ReadonlyArray<RunFlowTaskInfo>;
  readonly flowAllowedActions: FlowAllowedActions;
  readonly isServerOrSiteAdmin: boolean;
  readonly schedulingFlowsEnabled: boolean;
};

export class FlowOverviewDataGridView extends React.PureComponent<FlowOverviewDataGridViewProps> {

  private memoizedColumns = defaultMemoize(createColumns);
  private memoizedGetRowData = defaultMemoize(getRowData);
  private updateFlowOverviewGridTimer: any;

  private sliceableFactory: SliceableFactory;

  constructor(props: FlowOverviewDataGridViewProps) {
    super(props);
    this.sliceableFactory = ServiceLocator.get<SliceableFactory>('SliceableFactory');
  }

  private emptyChild(): JSX.Element {
    return (
      <div className={'tb-data-grid-empty'}>
        <div className={'tb-data-grid-empty-title'}>{Messages.flowOutputsEmptyMessage()}</div>
      </div>
    );
  }

  private getIsIncrementalEnabled(): boolean {
    return !!this.props.outputStepInfos.find(step => step.isIncrementalEnabled);
  }

  public componentDidMount() {
    if(FeatureFlags.autoUpdateFlowGrids) {
      this.updateFlowOverviewGridTimer =
      setInterval(this.props.updateData, updateFlowOverviewGridIntervalSeconds * 1000);
    }
  }

  public componentWillUnmount() {
    if(FeatureFlags.autoUpdateFlowGrids) {
      clearInterval(this.updateFlowOverviewGridTimer);
    }
  }

  public render(): JSX.Element {
    const columns = this.memoizedColumns(
      this.props.flowInfo,
      this.props.flowAllowedActions,
      this.props.isServerOrSiteAdmin,
      this.props.siteUrls,
      this.props.updateData,
      this.getIsIncrementalEnabled(),
      this.props.schedulingFlowsEnabled
    );
    const sliceable = this.memoizedGetRowData(
      this.sliceableFactory,
      this.props.flowInfo,
      this.props.runFlowTasks,
      this.props.outputStepInfos,
      this.props.outputStepRuns
    );
    // Calculate the height of the rows of the outputs. If there are no outputs, set height to show the empty outputs message
    const heightStyle: React.CSSProperties = {
      height: this.props.outputStepInfos.length ? getHeightForRows(this.props.outputStepInfos.length + 1) : 160
    };
    return (
      <div className={rootClass} style={heightStyle}>
        <SortableDataGrid
          columns={columns}
          getSliceable={sliceable}
          emptyChild={this.emptyChild()}
        />
      </div>
    );
  }
}
